import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  actualizarClinicaAdmin,
  actualizarConfiguracionesAdmin,
  borrarClinicaAdmin,
  cambiarEstadoUsuarioAdmin,
  cambiarRolUsuarioAdmin,
  crearClinicaAdmin,
  dispararJobNotificacionesAdmin,
  guardarProfesionalAdmin,
  listarClinicasAdmin,
  listarProfesionalesAdmin,
  listarUsuariosAdmin,
  obtenerConfiguracionesAdmin,
  obtenerCuentaActual,
  obtenerDiagnosticoAdmin,
  obtenerNotificacionesAdmin,
  obtenerUsuarioAdmin,
  verificarProfesionalAdmin,
  RolGlobal,
  type ClinicaAdminDto,
  type GuardarClinicaRequest,
  type GuardarPerfilProfesionalRequest,
  type PerfilProfesionalAdminDto,
} from "../api";
import Campo from "../componentes/Campo";
import { formatearFecha } from "../formato";

type PestanaAdmin = "usuarios" | "profesionales" | "clinicas" | "configuracion" | "diagnostico" | "notificaciones";

export default function ModuloAdmin() {
  const [pestana, setPestana] = useState<PestanaAdmin>("usuarios");
  const cuenta = useQuery({ queryKey: ["cuenta"], queryFn: obtenerCuentaActual });

  if (cuenta.isPending) return <p className="muted">Cargando módulo de administración…</p>;
  if (!cuenta.data || !cuenta.data.esAdministrador) {
    return (
      <div className="panel vacio">
        <h2>Acceso restringido</h2>
        <p className="muted">
          Este módulo está reservado exclusivamente para los administradores del sistema.
        </p>
        <Link className="boton" to="/">
          Volver a Mis Mascotas
        </Link>
      </div>
    );
  }

  return (
    <div className="modulo-datos-contenedor">
      <div className="titulo-fila">
        <div>
          <h1>Módulo de Administración</h1>
          <p className="lede">Gestión de usuarios, veterinarios, clínicas y parámetros globales.</p>
        </div>
      </div>

      <nav className="pestanas-barra" aria-label="Secciones de administración">
        <button
          type="button"
          className={`pestana-boton ${pestana === "usuarios" ? "activa" : ""}`}
          onClick={() => setPestana("usuarios")}
        >
          👥 Usuarios y Roles
        </button>
        <button
          type="button"
          className={`pestana-boton ${pestana === "profesionales" ? "activa" : ""}`}
          onClick={() => setPestana("profesionales")}
        >
          🩺 Veterinarios
        </button>
        <button
          type="button"
          className={`pestana-boton ${pestana === "clinicas" ? "activa" : ""}`}
          onClick={() => setPestana("clinicas")}
        >
          🏥 Clínicas
        </button>
        <button
          type="button"
          className={`pestana-boton ${pestana === "configuracion" ? "activa" : ""}`}
          onClick={() => setPestana("configuracion")}
        >
          ⚙️ Configuración
        </button>
        <button
          type="button"
          className={`pestana-boton ${pestana === "diagnostico" ? "activa" : ""}`}
          onClick={() => setPestana("diagnostico")}
        >
          📊 Diagnóstico
        </button>
        <button
          type="button"
          className={`pestana-boton ${pestana === "notificaciones" ? "activa" : ""}`}
          onClick={() => setPestana("notificaciones")}
        >
          🔔 Notificaciones & Hangfire
        </button>
      </nav>

      {pestana === "usuarios" && <SeccionUsuarios cuentaActualID={cuenta.data.usuarioID} />}
      {pestana === "profesionales" && <SeccionProfesionales />}
      {pestana === "clinicas" && <SeccionClinicas />}
      {pestana === "configuracion" && <SeccionConfiguracion />}
      {pestana === "diagnostico" && <SeccionDiagnostico />}
      {pestana === "notificaciones" && <SeccionNotificaciones />}
    </div>
  );
}

/* ========================================================================= */
/* 1. SECCIÓN USUARIOS Y ROLES                                              */
/* ========================================================================= */

function SeccionUsuarios({ cuentaActualID }: { cuentaActualID: string }) {
  const qc = useQueryClient();
  const [busqueda, setBusqueda] = useState("");
  const [filtroRol, setFiltroRol] = useState<RolGlobal | "">("");
  const [filtroActivo, setFiltroActivo] = useState<boolean | "">("");
  const [usuarioSeleccionadoID, setUsuarioSeleccionadoID] = useState<string | null>(null);

  const usuarios = useQuery({
    queryKey: ["admin", "usuarios", busqueda, filtroRol, filtroActivo],
    queryFn: () =>
      listarUsuariosAdmin({
        q: busqueda || undefined,
        rolGlobal: filtroRol === "" ? undefined : filtroRol,
        activo: filtroActivo === "" ? undefined : filtroActivo,
      }),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div className="panel" style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: "1 1 14rem" }}>
          <input
            type="search"
            placeholder="Buscar por nombre, apellido o email…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div style={{ minWidth: "10rem" }}>
          <select
            value={filtroRol}
            onChange={(e) => setFiltroRol(e.target.value ? (Number(e.target.value) as RolGlobal) : "")}
          >
            <option value="">Todos los roles</option>
            <option value={RolGlobal.Persona}>Persona</option>
            <option value={RolGlobal.Curador}>Curador</option>
            <option value={RolGlobal.Administrador}>Administrador</option>
          </select>
        </div>
        <div style={{ minWidth: "9rem" }}>
          <select
            value={filtroActivo === "" ? "" : filtroActivo ? "true" : "false"}
            onChange={(e) => setFiltroActivo(e.target.value === "" ? "" : e.target.value === "true")}
          >
            <option value="">Todos los estados</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      {usuarios.isPending && <p className="muted">Cargando usuarios…</p>}
      {usuarios.error && <p className="err">{usuarios.error.message}</p>}

      {usuarios.data && (
        <div className="panel tabla-responsive" style={{ padding: "0.5rem" }}>
          <table className="tabla-datos">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>Rol Global</th>
                <th>Estado</th>
                <th>Mascotas</th>
                <th>Perfil Vet</th>
                <th style={{ textAlign: "right" }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.data.map((u) => (
                <tr key={u.usuarioID}>
                  <td>
                    <strong>{u.nombreCompleto}</strong>
                    {u.usuarioID === cuentaActualID && (
                      <span className="etiqueta neutra" style={{ marginLeft: "0.4rem" }}>
                        Tú
                      </span>
                    )}
                  </td>
                  <td className="muted">{u.email}</td>
                  <td>
                    <span
                      className={`etiqueta ${
                        u.rolGlobal === RolGlobal.Administrador
                          ? "vencido"
                          : u.rolGlobal === RolGlobal.Curador
                          ? "por-vencer"
                          : "neutra"
                      }`}
                    >
                      {u.rolGlobal === RolGlobal.Administrador
                        ? "Administrador"
                        : u.rolGlobal === RolGlobal.Curador
                        ? "Curador"
                        : "Persona"}
                    </span>
                  </td>
                  <td>
                    <span className={`etiqueta ${u.activo ? "exito" : "neutra"}`}>
                      {u.activo ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td>{u.cantidadMascotas}</td>
                  <td>
                    {u.esVeterinario ? (
                      <span className={`etiqueta ${u.veterinarioVerificado ? "exito" : "por-vencer"}`}>
                        {u.veterinarioVerificado ? "✓ Verificado" : "Pendiente"}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className="boton-chico"
                      onClick={() => setUsuarioSeleccionadoID(u.usuarioID)}
                    >
                      Detalles y Rol
                    </button>
                  </td>
                </tr>
              ))}
              {usuarios.data.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted" style={{ textAlign: "center", padding: "1.5rem" }}>
                    No se encontraron usuarios con los filtros aplicados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {usuarioSeleccionadoID && (
        <ModalDetalleUsuario
          usuarioID={usuarioSeleccionadoID}
          cuentaActualID={cuentaActualID}
          alCerrar={() => {
            setUsuarioSeleccionadoID(null);
            qc.invalidateQueries({ queryKey: ["admin", "usuarios"] });
          }}
        />
      )}
    </div>
  );
}

function ModalDetalleUsuario({
  usuarioID,
  cuentaActualID,
  alCerrar,
}: {
  usuarioID: string;
  cuentaActualID: string;
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const detalle = useQuery({
    queryKey: ["admin", "usuario", usuarioID],
    queryFn: () => obtenerUsuarioAdmin(usuarioID),
  });

  const mutacionRol = useMutation({
    mutationFn: (nuevoRol: RolGlobal) => cambiarRolUsuarioAdmin(usuarioID, nuevoRol),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "usuario", usuarioID] });
      qc.invalidateQueries({ queryKey: ["admin", "usuarios"] });
    },
  });

  const mutacionEstado = useMutation({
    mutationFn: (nuevoEstado: boolean) => cambiarEstadoUsuarioAdmin(usuarioID, nuevoEstado),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "usuario", usuarioID] });
      qc.invalidateQueries({ queryKey: ["admin", "usuarios"] });
    },
  });

  const esMiCuenta = usuarioID === cuentaActualID;

  return (
    <div className="modal-fondo" onClick={alCerrar}>
      <div className="panel modal-caja" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "40rem" }}>
        {detalle.isPending && <p className="muted">Cargando detalles…</p>}
        {detalle.error && <p className="err">{detalle.error.message}</p>}

        {detalle.data && (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <div className="titulo-fila">
              <div>
                <h2>{detalle.data.nombreCompleto}</h2>
                <p className="muted" style={{ margin: 0 }}>
                  {detalle.data.email}
                </p>
              </div>
              <button type="button" className="boton-chico" onClick={alCerrar}>
                ✕ Cerrar
              </button>
            </div>

            <div className="fila" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div className="campo">
                <span className="campo-etiqueta">Rol Global</span>
                <select
                  value={detalle.data.rolGlobal}
                  disabled={esMiCuenta || mutacionRol.isPending}
                  onChange={(e) => mutacionRol.mutate(Number(e.target.value) as RolGlobal)}
                >
                  <option value={RolGlobal.Persona}>Persona</option>
                  <option value={RolGlobal.Curador}>Curador (Gestor de Catálogos)</option>
                  <option value={RolGlobal.Administrador}>Administrador (Operador del Sistema)</option>
                </select>
                {esMiCuenta && (
                  <span className="campo-ayuda">No puedes degradar tu propio rol de Administrador.</span>
                )}
                {mutacionRol.isSuccess && <span className="campo-ayuda" style={{ color: "var(--exito)" }}>Rol actualizado</span>}
              </div>

              <div className="campo">
                <span className="campo-etiqueta">Estado de la cuenta</span>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <button
                    type="button"
                    className={detalle.data.activo ? "boton-peligro" : "boton-primario"}
                    disabled={esMiCuenta || mutacionEstado.isPending}
                    onClick={() => mutacionEstado.mutate(!detalle.data.activo)}
                  >
                    {detalle.data.activo ? "Desactivar cuenta" : "Reactivar cuenta"}
                  </button>
                  <span className={`etiqueta ${detalle.data.activo ? "exito" : "neutra"}`}>
                    {detalle.data.activo ? "Activo" : "Inactivo"}
                  </span>
                </div>
                {esMiCuenta && (
                  <span className="campo-ayuda">No puedes desactivar tu propia cuenta.</span>
                )}
              </div>
            </div>

            {detalle.data.perfilProfesional && (
              <div className="bloque">
                <h3>Perfil Profesional (Veterinario)</h3>
                <dl className="datos">
                  <dt>Matrícula / Reg.</dt>
                  <dd>{detalle.data.perfilProfesional.numeroRegistro || "Sin registro"}</dd>
                  <dt>Título</dt>
                  <dd>{detalle.data.perfilProfesional.titulo || "Sin título"}</dd>
                  <dt>Clínica</dt>
                  <dd>{detalle.data.perfilProfesional.clinicaNombre || "Independiente"}</dd>
                  <dt>Verificación</dt>
                  <dd>
                    <span className={`etiqueta ${detalle.data.perfilProfesional.verificado ? "exito" : "por-vencer"}`}>
                      {detalle.data.perfilProfesional.verificado ? "✓ Verificado" : "Pendiente"}
                    </span>
                  </dd>
                </dl>
              </div>
            )}

            <div className="bloque">
              <h3>Mascotas asociadas ({detalle.data.mascotas.length})</h3>
              {detalle.data.mascotas.length === 0 ? (
                <p className="muted" style={{ margin: 0 }}>
                  Este usuario no tiene mascotas asociadas.
                </p>
              ) : (
                <ul className="recordatorios">
                  {detalle.data.mascotas.map((m) => (
                    <li key={m.mascotaID} className="recordatorio al-dia" style={{ justifyContent: "space-between" }}>
                      <span>
                        <strong>{m.nombreMascota}</strong> ({m.especie})
                      </span>
                      <span className="etiqueta neutra">
                        {m.rol === 1 ? "Dueño" : m.rol === 3 ? "Veterinario" : "Acceso"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="muted" style={{ fontSize: "0.8rem", borderTop: "1px solid var(--rule)", paddingTop: "0.5rem" }}>
              Registrado: {formatearFecha(detalle.data.creadoUtc)} · Último cambio: {formatearFecha(detalle.data.modificadoUtc)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ========================================================================= */
/* 2. SECCIÓN VETERINARIOS Y PERFILES PROFESIONALES                          */
/* ========================================================================= */

function SeccionProfesionales() {
  const qc = useQueryClient();
  const [busqueda, setBusqueda] = useState("");
  const [filtroVerificado, setFiltroVerificado] = useState<boolean | "">("");
  const [perfilParaEditar, setPerfilParaEditar] = useState<PerfilProfesionalAdminDto | null>(null);

  const profesionales = useQuery({
    queryKey: ["admin", "profesionales", busqueda, filtroVerificado],
    queryFn: () =>
      listarProfesionalesAdmin({
        q: busqueda || undefined,
        verificado: filtroVerificado === "" ? undefined : filtroVerificado,
      }),
  });

  const mutacionVerificar = useMutation({
    mutationFn: ({ usuarioID, verificado }: { usuarioID: string; verificado: boolean }) =>
      verificarProfesionalAdmin(usuarioID, verificado),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "profesionales"] });
      qc.invalidateQueries({ queryKey: ["admin", "usuarios"] });
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div className="panel" style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: "1 1 14rem" }}>
          <input
            type="search"
            placeholder="Buscar veterinario por nombre, matrícula o clínica…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div style={{ minWidth: "12rem" }}>
          <select
            value={filtroVerificado === "" ? "" : filtroVerificado ? "true" : "false"}
            onChange={(e) => setFiltroVerificado(e.target.value === "" ? "" : e.target.value === "true")}
          >
            <option value="">Todas las verificaciones</option>
            <option value="true">Solo Verificados</option>
            <option value="false">Solo Pendientes</option>
          </select>
        </div>
      </div>

      {profesionales.isPending && <p className="muted">Cargando veterinarios…</p>}
      {profesionales.error && <p className="err">{profesionales.error.message}</p>}

      {profesionales.data && (
        <div className="panel tabla-responsive" style={{ padding: "0.5rem" }}>
          <table className="tabla-datos">
            <thead>
              <tr>
                <th>Profesional</th>
                <th>Email</th>
                <th>Matrícula / Reg.</th>
                <th>Título</th>
                <th>Clínica</th>
                <th>Estado</th>
                <th style={{ textAlign: "right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {profesionales.data.map((p) => (
                <tr key={p.usuarioID}>
                  <td>
                    <strong>{p.nombreCompleto}</strong>
                  </td>
                  <td className="muted">{p.email}</td>
                  <td>{p.numeroRegistro || <span className="muted">—</span>}</td>
                  <td>{p.titulo || <span className="muted">—</span>}</td>
                  <td>{p.clinicaNombre || <span className="muted">Independiente</span>}</td>
                  <td>
                    <span className={`etiqueta ${p.verificado ? "exito" : "por-vencer"}`}>
                      {p.verificado ? "✓ Verificado" : "Pendiente"}
                    </span>
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      className="boton-chico"
                      style={{ marginRight: "0.4rem" }}
                      onClick={() => setPerfilParaEditar(p)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className={`boton-chico ${p.verificado ? "boton-peligro" : "boton-primario"}`}
                      disabled={mutacionVerificar.isPending}
                      onClick={() =>
                        mutacionVerificar.mutate({
                          usuarioID: p.usuarioID,
                          verificado: !p.verificado,
                        })
                      }
                    >
                      {p.verificado ? "Revocar" : "Verificar"}
                    </button>
                  </td>
                </tr>
              ))}
              {profesionales.data.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted" style={{ textAlign: "center", padding: "1.5rem" }}>
                    No hay profesionales registrados con los criterios seleccionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {perfilParaEditar && (
        <ModalEditarProfesional
          perfil={perfilParaEditar}
          alCerrar={() => {
            setPerfilParaEditar(null);
            qc.invalidateQueries({ queryKey: ["admin", "profesionales"] });
          }}
        />
      )}
    </div>
  );
}

function ModalEditarProfesional({
  perfil,
  alCerrar,
}: {
  perfil: PerfilProfesionalAdminDto;
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const clinicas = useQuery({ queryKey: ["admin", "clinicas"], queryFn: () => listarClinicasAdmin() });

  const [numeroRegistro, setNumeroRegistro] = useState(perfil.numeroRegistro || "");
  const [titulo, setTitulo] = useState(perfil.titulo || "Médico Veterinario");
  const [clinicaID, setClinicaID] = useState<number | "">(perfil.clinicaID || "");
  const [verificado, setVerificado] = useState(perfil.verificado);

  const mutacion = useMutation({
    mutationFn: (req: GuardarPerfilProfesionalRequest) => guardarProfesionalAdmin(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "profesionales"] });
      alCerrar();
    },
  });

  return (
    <div className="modal-fondo" onClick={alCerrar}>
      <div className="panel modal-caja" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "32rem" }}>
        <h2>Editar Datos Profesionales</h2>
        <p className="muted" style={{ margin: "0 0 1rem" }}>
          {perfil.nombreCompleto} ({perfil.email})
        </p>

        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            mutacion.mutate({
              usuarioID: perfil.usuarioID,
              numeroRegistro: numeroRegistro || null,
              titulo: titulo || null,
              clinicaID: clinicaID === "" ? null : Number(clinicaID),
              verificado,
            });
          }}
        >
          <Campo etiqueta="Número de Matrícula / Registro Nacional">
            <input
              type="text"
              value={numeroRegistro}
              onChange={(e) => setNumeroRegistro(e.target.value)}
              placeholder="Ej: CMV-12345"
            />
          </Campo>

          <Campo etiqueta="Título Profesional">
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ej: Médico Veterinario"
            />
          </Campo>

          <Campo etiqueta="Clínica Asociada">
            <select
              value={clinicaID}
              onChange={(e) => setClinicaID(e.target.value === "" ? "" : Number(e.target.value))}
            >
              <option value="">Independiente / Sin clínica fija</option>
              {clinicas.data?.map((c) => (
                <option key={c.clinicaID} value={c.clinicaID}>
                  {c.nombre} {c.comuna ? `(${c.comuna})` : ""}
                </option>
              ))}
            </select>
          </Campo>

          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
            <input
              type="checkbox"
              id="verificado-check"
              checked={verificado}
              onChange={(e) => setVerificado(e.target.checked)}
              style={{ width: "auto" }}
            />
            <label htmlFor="verificado-check" style={{ fontWeight: 500, cursor: "pointer" }}>
              Acreditar como Veterinario Verificado
            </label>
          </div>

          {mutacion.error && <p className="err">{mutacion.error.message}</p>}

          <div className="acciones" style={{ marginTop: "1rem" }}>
            <button type="submit" className="boton-primario" disabled={mutacion.isPending}>
              {mutacion.isPending ? "Guardando…" : "Guardar Cambios"}
            </button>
            <button type="button" onClick={alCerrar}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ========================================================================= */
/* 3. SECCIÓN CLÍNICAS VETERINARIAS                                         */
/* ========================================================================= */

function SeccionClinicas() {
  const qc = useQueryClient();
  const [busqueda, setBusqueda] = useState("");
  const [modalClinicaAbierto, setModalClinicaAbierto] = useState(false);
  const [clinicaParaEditar, setClinicaParaEditar] = useState<ClinicaAdminDto | null>(null);

  const clinicas = useQuery({
    queryKey: ["admin", "clinicas", busqueda],
    queryFn: () => listarClinicasAdmin({ q: busqueda || undefined }),
  });

  const mutacionBorrar = useMutation({
    mutationFn: (clinicaID: number) => borrarClinicaAdmin(clinicaID),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "clinicas"] }),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div className="titulo-fila">
        <div style={{ flex: "1 1 14rem", maxWidth: "24rem" }}>
          <input
            type="search"
            placeholder="Buscar clínicas por nombre o comuna…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="boton-primario"
          onClick={() => {
            setClinicaParaEditar(null);
            setModalClinicaAbierto(true);
          }}
        >
          + Nueva Clínica
        </button>
      </div>

      {clinicas.isPending && <p className="muted">Cargando clínicas…</p>}
      {clinicas.error && <p className="err">{clinicas.error.message}</p>}

      {clinicas.data && (
        <div className="tarjetas">
          {clinicas.data.map((c) => (
            <div key={c.clinicaID} className="panel tarjeta">
              <header className="tarjeta-cab">
                <h3 style={{ margin: 0, fontSize: "1.1rem" }}>{c.nombre}</h3>
                <span className="etiqueta neutra">{c.cantidadProfesionales} prof.</span>
              </header>

              <dl className="datos">
                {c.comuna && (
                  <>
                    <dt>Comuna</dt>
                    <dd>{c.comuna}</dd>
                  </>
                )}
                {c.direccion && (
                  <>
                    <dt>Dirección</dt>
                    <dd>{c.direccion}</dd>
                  </>
                )}
                {c.telefono && (
                  <>
                    <dt>Teléfono</dt>
                    <dd>{c.telefono}</dd>
                  </>
                )}
              </dl>

              <div className="acciones" style={{ marginTop: "auto", paddingTop: "0.5rem" }}>
                <button
                  type="button"
                  className="boton-chico"
                  onClick={() => {
                    setClinicaParaEditar(c);
                    setModalClinicaAbierto(true);
                  }}
                >
                  Editar
                </button>
                <button
                  type="button"
                  className="boton-chico boton-peligro"
                  disabled={mutacionBorrar.isPending}
                  onClick={() => {
                    if (confirm(`¿Eliminar la clínica "${c.nombre}"?`)) {
                      mutacionBorrar.mutate(c.clinicaID);
                    }
                  }}
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
          {clinicas.data.length === 0 && (
            <div className="panel vacio" style={{ gridColumn: "1 / -1" }}>
              <p className="muted">No hay clínicas veterinarias registradas.</p>
            </div>
          )}
        </div>
      )}

      {modalClinicaAbierto && (
        <ModalClinicaForm
          clinica={clinicaParaEditar}
          alCerrar={() => {
            setModalClinicaAbierto(false);
            qc.invalidateQueries({ queryKey: ["admin", "clinicas"] });
          }}
        />
      )}
    </div>
  );
}

function ModalClinicaForm({
  clinica,
  alCerrar,
}: {
  clinica: ClinicaAdminDto | null;
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState(clinica?.nombre || "");
  const [direccion, setDireccion] = useState(clinica?.direccion || "");
  const [comuna, setComuna] = useState(clinica?.comuna || "");
  const [telefono, setTelefono] = useState(clinica?.telefono || "");

  const mutacion = useMutation({
    mutationFn: (dto: GuardarClinicaRequest) =>
      clinica ? actualizarClinicaAdmin(clinica.clinicaID, dto) : crearClinicaAdmin(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "clinicas"] });
      alCerrar();
    },
  });

  return (
    <div className="modal-fondo" onClick={alCerrar}>
      <div className="panel modal-caja" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "32rem" }}>
        <h2>{clinica ? "Editar Clínica" : "Nueva Clínica Veterinaria"}</h2>

        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            mutacion.mutate({
              nombre,
              direccion: direccion || null,
              comuna: comuna || null,
              telefono: telefono || null,
            });
          }}
        >
          <Campo etiqueta="Nombre de la Clínica" requerido>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Clínica Veterinaria Los Andes"
              required
            />
          </Campo>

          <Campo etiqueta="Comuna">
            <input
              type="text"
              value={comuna}
              onChange={(e) => setComuna(e.target.value)}
              placeholder="Ej: Providencia, Las Condes..."
            />
          </Campo>

          <Campo etiqueta="Dirección">
            <input
              type="text"
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
              placeholder="Ej: Av. Los Leones 123"
            />
          </Campo>

          <Campo etiqueta="Teléfono de Contacto">
            <input
              type="tel"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="Ej: +56 9 1234 5678"
            />
          </Campo>

          {mutacion.error && <p className="err">{mutacion.error.message}</p>}

          <div className="acciones" style={{ marginTop: "1rem" }}>
            <button type="submit" className="boton-primario" disabled={mutacion.isPending}>
              {mutacion.isPending ? "Guardando…" : clinica ? "Guardar Cambios" : "Crear Clínica"}
            </button>
            <button type="button" onClick={alCerrar}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ========================================================================= */
/* 4. SECCIÓN CONFIGURACIÓN GLOBAL                                          */
/* ========================================================================= */

function SeccionConfiguracion() {
  const qc = useQueryClient();
  const configuraciones = useQuery({
    queryKey: ["admin", "configuracion"],
    queryFn: obtenerConfiguracionesAdmin,
  });

  const [valores, setValores] = useState<Record<string, string>>({});
  const [editando, setEditando] = useState(false);

  // Inicializar estado local cuando cargan los datos
  if (configuraciones.data && !editando && Object.keys(valores).length === 0) {
    const inicial: Record<string, string> = {};
    configuraciones.data.forEach((c) => {
      inicial[c.clave] = c.valor;
    });
    setValores(inicial);
  }

  const mutacion = useMutation({
    mutationFn: () => actualizarConfiguracionesAdmin(valores),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "configuracion"] });
      setEditando(false);
    },
  });

  return (
    <div className="panel" style={{ maxWidth: "45rem" }}>
      <h2>Parámetros Globales del Sistema</h2>
      <p className="muted">
        Ajusta las reglas de negocio y límites operativos sin requerir despliegues ni cambios de código.
      </p>

      {configuraciones.isPending && <p className="muted">Cargando parámetros…</p>}
      {configuraciones.error && <p className="err">{configuraciones.error.message}</p>}

      {configuraciones.data && (
        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            mutacion.mutate();
          }}
        >
          {configuraciones.data.map((c) => (
            <div key={c.clave} className="campo">
              <label className="campo-etiqueta" style={{ fontWeight: 600 }}>
                {c.clave}
              </label>
              {c.descripcion && <span className="campo-ayuda">{c.descripcion}</span>}
              <input
                type="text"
                value={valores[c.clave] ?? c.valor}
                onChange={(e) => {
                  setEditando(true);
                  setValores({ ...valores, [c.clave]: e.target.value });
                }}
              />
            </div>
          ))}

          {mutacion.error && <p className="err">{mutacion.error.message}</p>}
          {mutacion.isSuccess && (
            <p className="aviso" style={{ background: "var(--exito-suave)", color: "var(--exito)" }}>
              ✓ Parámetros actualizados con éxito en la base de datos.
            </p>
          )}

          <div className="acciones" style={{ marginTop: "1rem" }}>
            <button type="submit" className="boton-primario" disabled={mutacion.isPending}>
              {mutacion.isPending ? "Guardando…" : "Guardar Configuraciones"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ========================================================================= */
/* 5. SECCIÓN DIAGNÓSTICO Y MÉTRICAS OPERATIVAS                             */
/* ========================================================================= */

function SeccionDiagnostico() {
  const diagnostico = useQuery({
    queryKey: ["admin", "diagnostico"],
    queryFn: obtenerDiagnosticoAdmin,
    refetchInterval: 15_000,
  });

  if (diagnostico.isPending) return <p className="muted">Recopilando métricas del sistema…</p>;
  if (diagnostico.error) return <p className="err">{diagnostico.error.message}</p>;

  const d = diagnostico.data;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Barra de Estado de Infraestructura */}
      <div
        className="panel"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
          background: "var(--surface)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <span style={{ fontSize: "1.5rem" }}>⚡</span>
          <div>
            <strong>Base de Datos:</strong>{" "}
            <span className={`etiqueta ${d.estadoBaseDatos === "conectada" ? "exito" : "vencido"}`}>
              {d.estadoBaseDatos}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "1.5rem", fontSize: "0.9rem", color: "var(--ink-2)" }}>
          <span>
            <strong>Entorno:</strong> {d.entorno}
          </span>
          <span>
            <strong>Hora Servidor:</strong> {new Date(d.servidorHoraUtc).toLocaleTimeString()} UTC
          </span>
        </div>
      </div>

      {/* Grid de Métricas Operativas */}
      <div className="tarjetas" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(16rem, 1fr))" }}>
        <div className="panel tarjeta">
          <span className="muted" style={{ fontSize: "0.85rem", textTransform: "uppercase", fontWeight: 600 }}>
            👥 Usuarios Registrados
          </span>
          <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "var(--ink)" }}>{d.totalUsuarios}</div>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", fontSize: "0.85rem" }}>
            <span className="etiqueta exito">{d.totalUsuariosActivos} activos</span>
            <span className="etiqueta por-vencer">{d.totalCuradores} curadores</span>
            <span className="etiqueta vencido">{d.totalAdministradores} admin</span>
          </div>
        </div>

        <div className="panel tarjeta">
          <span className="muted" style={{ fontSize: "0.85rem", textTransform: "uppercase", fontWeight: 600 }}>
            🐾 Mascotas Registradas
          </span>
          <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "var(--accent)" }}>{d.totalMascotas}</div>
          <div style={{ fontSize: "0.85rem", color: "var(--ink-2)" }}>
            <strong>{d.totalMascotasActivas}</strong> con estado activa
          </div>
        </div>

        <div className="panel tarjeta">
          <span className="muted" style={{ fontSize: "0.85rem", textTransform: "uppercase", fontWeight: 600 }}>
            🩺 Historial Clínico
          </span>
          <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "var(--ink)" }}>{d.totalVisitas}</div>
          <div style={{ fontSize: "0.85rem", color: "var(--ink-2)" }}>Visitas y atenciones registradas</div>
        </div>

        <div className="panel tarjeta">
          <span className="muted" style={{ fontSize: "0.85rem", textTransform: "uppercase", fontWeight: 600 }}>
            ⏰ Recordatorios de Salud
          </span>
          <div style={{ fontSize: "2.2rem", fontWeight: 800, color: d.totalRecordatoriosVencidos > 0 ? "var(--crit)" : "var(--exito)" }}>
            {d.totalRecordatoriosPendientes}
          </div>
          <div style={{ display: "flex", gap: "0.4rem", fontSize: "0.85rem" }}>
            {d.totalRecordatoriosVencidos > 0 ? (
              <span className="etiqueta vencido">{d.totalRecordatoriosVencidos} vencidos</span>
            ) : (
              <span className="etiqueta exito">Todos al día</span>
            )}
          </div>
        </div>

        <div className="panel tarjeta">
          <span className="muted" style={{ fontSize: "0.85rem", textTransform: "uppercase", fontWeight: 600 }}>
            📁 Archivos en Storage
          </span>
          <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "var(--ink)" }}>{d.totalAdjuntos}</div>
          <div style={{ fontSize: "0.85rem", color: "var(--ink-2)" }}>Fotos de perfil, recetas y carnéts</div>
        </div>
      </div>
    </div>
  );
}

/* ========================================================================= */
/* 6. SECCIÓN NOTIFICACIONES & HANGFIRE                                     */
/* ========================================================================= */

function SeccionNotificaciones() {
  const qc = useQueryClient();
  const notificaciones = useQuery({
    queryKey: ["admin", "notificaciones", "resumen"],
    queryFn: obtenerNotificacionesAdmin,
  });

  const [resultadoDisparo, setResultadoDisparo] = useState<string | null>(null);

  const mutacionDisparo = useMutation({
    mutationFn: dispararJobNotificacionesAdmin,
    onSuccess: (resp) => {
      setResultadoDisparo(`¡Trabajo encolado con éxito! ID Hangfire: ${resp.trabajoId}`);
      qc.invalidateQueries({ queryKey: ["admin", "notificaciones"] });
    },
    onError: (err: Error) => {
      setResultadoDisparo(`Error al encolar trabajo: ${err.message}`);
    },
  });

  if (notificaciones.isPending) return <p className="muted">Cargando estado del servicio de notificaciones…</p>;
  if (notificaciones.error) return <p className="err">{notificaciones.error.message}</p>;

  const n = notificaciones.data;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* Panel de Control y Acceso a Hangfire */}
      <div
        className="panel"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
          background: "var(--surface)",
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700 }}>
            🔔 Automatización de Recordatorios y Hangfire
          </h3>
          <p className="muted" style={{ margin: "0.3rem 0 0 0", fontSize: "0.9rem" }}>
            Supervisión del job diario de vencimientos, encolamiento manual y panel de control en vivo.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          <a
            href="/admin/hangfire"
            target="_blank"
            rel="noopener noreferrer"
            className="boton"
            style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
          >
            <span>📊</span> Abrir Dashboard Hangfire
          </a>
          <button
            type="button"
            className="boton primario"
            disabled={mutacionDisparo.isPending}
            onClick={() => {
              setResultadoDisparo(null);
              mutacionDisparo.mutate();
            }}
          >
            {mutacionDisparo.isPending ? "Encolando…" : "▶️ Ejecutar Revisión Ahora"}
          </button>
        </div>
      </div>

      {resultadoDisparo && (
        <div
          className="panel"
          style={{
            background: resultadoDisparo.includes("Error") ? "var(--crit-bg, #fee2e2)" : "var(--exito-bg, #ecfdf5)",
            border: `1px solid ${resultadoDisparo.includes("Error") ? "var(--crit, #ef4444)" : "var(--exito, #10b981)"}`,
            padding: "0.85rem 1.25rem",
            borderRadius: "0.5rem",
            fontSize: "0.95rem",
            fontWeight: 600,
          }}
        >
          {resultadoDisparo}
        </div>
      )}

      {/* Tarjetas de Estado de Notificaciones */}
      <div className="tarjetas" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(16rem, 1fr))" }}>
        <div className="panel tarjeta">
          <span className="muted" style={{ fontSize: "0.85rem", textTransform: "uppercase", fontWeight: 600 }}>
            📬 Proveedor de Correo
          </span>
          <div style={{ fontSize: "1.6rem", fontWeight: 800, color: "var(--ink)" }}>{n.proveedorEmail}</div>
          <div style={{ display: "flex", gap: "0.4rem", fontSize: "0.85rem" }}>
            <span className={`etiqueta ${n.emailHabilitado ? "exito" : "por-vencer"}`}>
              {n.emailHabilitado ? "Envíos Habilitados" : "Modo Simulado (Logs)"}
            </span>
          </div>
        </div>

        <div className="panel tarjeta">
          <span className="muted" style={{ fontSize: "0.85rem", textTransform: "uppercase", fontWeight: 600 }}>
            ⏳ Ventana de Anticipación
          </span>
          <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "var(--accent)" }}>
            {n.diasAvisoVencimiento} <span style={{ fontSize: "1rem", fontWeight: 500 }}>días</span>
          </div>
          <div style={{ fontSize: "0.85rem", color: "var(--ink-2)" }}>
            Configurado en Parámetros Globales
          </div>
        </div>

        <div className="panel tarjeta">
          <span className="muted" style={{ fontSize: "0.85rem", textTransform: "uppercase", fontWeight: 600 }}>
            ⚠️ Próximos a Vencer
          </span>
          <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "var(--advertencia, #f59e0b)" }}>
            {n.totalPorVencer}
          </div>
          <div style={{ fontSize: "0.85rem", color: "var(--ink-2)" }}>
            En los siguientes {n.diasAvisoVencimiento} días
          </div>
        </div>

        <div className="panel tarjeta">
          <span className="muted" style={{ fontSize: "0.85rem", textTransform: "uppercase", fontWeight: 600 }}>
            🔴 Vencidos Pendientes
          </span>
          <div style={{ fontSize: "2.2rem", fontWeight: 800, color: n.totalVencidos > 0 ? "var(--crit)" : "var(--exito)" }}>
            {n.totalVencidos}
          </div>
          <div style={{ fontSize: "0.85rem", color: "var(--ink-2)" }}>
            Fecha programada anterior a hoy
          </div>
        </div>

        <div className="panel tarjeta">
          <span className="muted" style={{ fontSize: "0.85rem", textTransform: "uppercase", fontWeight: 600 }}>
            📋 Total Recordatorios Pendientes
          </span>
          <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "var(--ink)" }}>
            {n.totalPendientes}
          </div>
          <div style={{ fontSize: "0.85rem", color: "var(--ink-2)" }}>
            En seguimiento clínico
          </div>
        </div>

        <div className="panel tarjeta">
          <span className="muted" style={{ fontSize: "0.85rem", textTransform: "uppercase", fontWeight: 600 }}>
            ✅ Recordatorios Notificados
          </span>
          <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "var(--exito)" }}>
            {n.totalNotificados}
          </div>
          <div style={{ fontSize: "0.85rem", color: "var(--ink-2)" }}>
            Con fecha de aviso registrada
          </div>
        </div>
      </div>

      {/* Explicación de Funcionamiento */}
      <div className="panel" style={{ background: "var(--surface)", fontSize: "0.9rem", lineHeight: 1.6 }}>
        <h4 style={{ margin: "0 0 0.5rem 0", color: "var(--ink)" }}>ℹ️ Cómo funciona el servicio de notificaciones:</h4>
        <ul style={{ margin: 0, paddingLeft: "1.2rem", color: "var(--ink-2)" }}>
          <li>
            <strong>Job Recurrente Diario:</strong> Programado automáticamente en Hangfire para ejecutarse todos los días a las <strong>08:00 UTC</strong> (05:00 hora Chile).
          </li>
          <li>
            <strong>Agrupación por Dueño:</strong> Si una mascota tiene múltiples vacunas o un dueño tiene varias mascotas con vencimientos próximos, se envía <strong>un único correo consolidado</strong> con la lista completa.
          </li>
          <li>
            <strong>Deduplicación:</strong> Cada recordatorio registra su fecha en <code>NotificadoUtc</code> al despacharse, evitando envíos duplicados al dueño.
          </li>
          <li>
            <strong>Reintentos Automáticos:</strong> Ante cualquier falla transitoria del servidor de correo, Hangfire reintenta la tarea de manera exponencial sin intervención manual.
          </li>
        </ul>
      </div>
    </div>
  );
}
